<template>
  <div class="forms">
    <v-progress-linear class="forms__progress" background-opacity="0" color="#0AAC5F" :value="processValue"/>
    <div class="forms__main">
      <v-btn elevation="0" class="forms__backbtn" :disabled="currentStep === 1" @click="handleBack">
        <v-icon size="16">mdi-arrow-left</v-icon>
      </v-btn>
      <div class="forms__title">{{ processTitle }}</div>
      <div class="forms__title-tip" v-if="currentStep === 7">
        <img src="@/assets/imgs/form-title-tip.svg"/>
        No compartiremos tu información con terceros
      </div>
      <v-form
          class="forms__detail"
          ref="form"
      >
        <template v-if="currentStep === 1">
          <div class="forms__detail-base">
            <v-select
                v-model="formValue.carBrand"
                :items="brandOptions"
                label="Marca"
                hide-details
                filled
                @change="handleBrandChange"
                append-icon="mdi-chevron-down"
            />
            <v-select
                v-model="formValue.carModel"
                :items="modelOptions"
                label="Modelo"
                hide-details
                filled
                @change="handleModelChange"
                append-icon="mdi-chevron-down"
            />
          </div>
        </template>
        <!--        买车时间、首付、月收入、信用评价、是否考虑二手车-->
        <template v-else-if="isRadioStep">
          <radio-options
              :options="currentOptions"
              :value="currentValue"
              @input="handleRadioInput"
          />
        </template>
        <!--        其他信息-->
        <template v-else-if="currentStep === 7">
          <div class="forms__detail-base">
            <div class="phone-input-wrap">
              <div class="phone-input-main" :class="{ 'is-focused': isFocused, 'has-value': formValue.phoneNo }">
                <label class="phone-input-label">Número celular</label>
                <input
                    maxlength="10"
                    type="text"
                    :value="formValue.phoneNo"
                    @input="handleInput"
                    @focus="isFocused = true"
                    @blur="isFocused = false"
                    class="phone-native-input"
                />

                <!-- 3. 后缀/计数器 -->
                <div class="phone-input-suffix">
                  {{ (formValue.phoneNo || '').length }}/10
                </div>
              </div>

            </div>
            <v-text-field
                label="Nombre y Apellido"
                filled
                hide-details
                v-model="formValue.userName"
                @blur="(e)=>handleBlur(e,'userName')"
            />
            <v-text-field
                label="Correo electrónico (Opcional)"
                filled
                hide-details
                v-model="formValue.email"
                @blur="(e)=>handleBlur(e,'email')"
            />
          </div>
        </template>
      </v-form>
      <div class="forms__protocol" v-if="currentStep === 7">
        <img ref="reference" class="forms__protocol-img"
             :src="require(isChecked ?'@/assets/imgs/protocol-check.svg' : '@/assets/imgs/protocol-uncheck.svg')"
             @click="handleProtocolClick"/>
        <transition name="tooltip-fade">
          <div
              v-if="visible"
              ref="floating"
              class="forms__protocol-tooltip"
          >
            Por favor, acepte
          </div>
        </transition>
        <div class="forms__protocol-text">
          Al enviar acepto el <a :href="`${domain}/privacy`" target="_blank"
                                 data-track-widget-id="privacy-link"
                                 data-track-widget-label="Aviso de Privacidad">Aviso de Privacidad</a> y
          los
          <a
              :href="`${domain}/terms`"
              target="_blank"
              data-track-widget-id="terms-link"
              data-track-widget-label="Términos y Condiciones">Términos y
            Condiciones</a>.
        </div>

      </div>
      <div class="forms__main-submit" v-if="currentStep === 1 || currentStep === 7">
        <v-btn elevation="0" block color="#0AAC5F" @click="validate">{{ confirmBtnText }}</v-btn>
      </div>
    </div>
    <v-snackbar
        v-model="snackbar"
        absolute
        centered
        class="forms__snackbar"
        color="rgba(12, 21, 44, 0.80)"
        timeout="1500"
    >
      {{ snackbarText }}
    </v-snackbar>
    <repeat-dialog
        :visible.sync="repeatDialogVisible"
    />
    <success-dialog
        :visible.sync="successDialogVisible"
        @confirm="handleSuccessConfirm"
    />
    <v-overlay :value="loading">
      <v-progress-circular color="primary" indeterminate size="48"/>
    </v-overlay>
  </div>
</template>

<script>
import CarCategory from '@/assets/json/carCategory.json'
import RadioOptions from "@/views/info-form/components/radio-options.vue"
import {ERROR_TYPES, EVENTS} from "@/utils/tracker";
import RepeatDialog from "@/components/repeat-dialog.vue";
import SuccessDialog from "@/components/success-dialog.vue";
import CryptoJS from 'crypto-js'
import axios from 'axios'
import {
  computePosition,
  offset,
  flip,
  shift,
  autoUpdate
} from '@floating-ui/dom'

const STEP_CONFIG = [
  {
    step: 1,
    title: '¿Qué auto deseas estrenar?',
    fields: ['carBrand', 'carModel'],
    isComplete: (form) => !!form.carBrand && !!form.carModel,
    validate: (form, ctx) => {
      if (!form.carBrand || !form.carModel) return false
      const brand = ctx.brandOptions.find(b => b.value === form.carBrand)
      if (!brand) return false
      return brand.children.some(m => m.value === form.carModel)
    }
  },
  {
    step: 2,
    title: '¿Cuándo deseas comprar tu auto?',
    fields: ['wantBuyTime'],
    isComplete: (form) => !!form.wantBuyTime,
    optionsKey: 'timeOptions', // 用于合法性校验
  },
  {
    step: 3,
    title: '¿Cuál es el monto máximo que puedes destinar para el pago inicial?',
    fields: ['downPaymentAmount'],
    isComplete: (form) => !!form.downPaymentAmount,
    optionsKey: 'downPaymentOptions',
  },
  {
    step: 4,
    title: '¿Cuál es aproximadamente tu ingreso mensual?',
    fields: ['monthlyIncome'],
    isComplete: (form) => !!form.monthlyIncome,
    optionsKey: 'monthlyIncomeOptions',
  },
  {
    step: 5,
    title: '¿Cómo es tu historial crediticio?',
    fields: ['credit'],
    isComplete: (form) => !!form.credit,
    optionsKey: 'creditOptions',
  },
  {
    step: 6,
    title: '¿Estás considerando un auto seminuevo como opción?',
    fields: ['consider'],
    isComplete: (form) => !!form.consider,
    optionsKey: 'considerOptions',
  },
  {
    step: 7,
    title: 'Déjanos tus datos de contacto',
    fields: ['phoneNo', 'email', 'userName'],
    isComplete: () => false, // 最后一步不参与
  },
]

export default {
  name: "info-form",
  components: {SuccessDialog, RepeatDialog, RadioOptions},
  data() {
    return {
      STEP_CONFIG,
      visible: false,
      cleanup: null,
      currentStep: 1,
      totalStep: 7,
      isFocused: false,
      brandOptions: CarCategory,
      modelOptions: [],
      loading: false,
      isChecked: false,
      repeatDialogVisible: false,
      successDialogVisible: false,
      snackbar: false,
      snackbarText: "",
      domain: process.env.VUE_APP_DOMAIN,
      formValue: {
        carBrand: null,
        carModel: null,
        wantBuyTime: "",
        downPaymentAmount: "",
        monthlyIncome: "",
        credit: "",
        consider: "",
        phoneNo: '',
        userName: '',
        email: '',
      },
      oldFormValue: {},
      timeOptions: [
        {label: 'En 1 a 2 semanas', value: 'En 1 a 2 semanas'},
        {label: 'En 1 mes', value: 'En 1 mes'},
        {label: 'Dentro de 3 meses', value: 'Dentro de 3 meses'},
        {label: 'No tengo una fecha definida', value: 'No tengo una fecha definida'},
      ],
      downPaymentOptions: [
        {label: 'Prefiero un plan sin pago inicial', value: 'Prefiero un plan sin pago inicial'},
        {label: 'Entre $1,000 y $30,000', value: 'Entre $1,000 y $30,000'},
        {label: '$30,000 – $50,000', value: '$30,000 – $50,000'},
        {label: '$50,000 – $100,000', value: '$50,000 – $100,000'},
        {label: 'Más de $100,000', value: 'Más de $100,000'},
      ],
      monthlyIncomeOptions: [
        {label: 'No tengo ingresos estables', value: 'No tengo ingresos estables'},
        {label: 'Menos de $10,000', value: 'Menos de $10,000'},
        {label: '$10,000 – $20,000', value: '$10,000 – $20,000'},
        {label: 'Más de $20,000', value: 'Más de $20,000'},
      ],
      creditOptions: [
        {
          label: 'He tenido crédito y siempre he pagado a tiempo.',
          value: 'He tenido crédito y siempre he pagado a tiempo.'
        },
        {
          label: 'He tenido crédito y he tenido algunos pagos atrasados en el pasado.',
          value: 'He tenido crédito y he tenido algunos pagos atrasados en el pasado.'
        },
        {
          label: 'He tenido crédito y he tenido muchos pagos atrasados o cuentas sin pagar.',
          value: 'He tenido crédito y he tenido muchos pagos atrasados o cuentas sin pagar.'
        },
        {label: 'Nunca he tenido crédito.', value: 'Nunca he tenido crédito.'},
      ],
      considerOptions: [
        {label: 'Sí', value: 'Sí'},
        {label: 'No', value: 'No'},
        {label: 'Depende de las condiciones', value: 'Depende de las condiciones'},
      ],
    }
  },
  computed: {
    processValue() {
      return (this.currentStep / this.totalStep) * 100
    },
    processTitle() {
      return this.STEP_CONFIG.find(s => s.step === this.currentStep)?.title || ''
    },
    isRadioStep() {
      return this.currentStep >= 2 && this.currentStep <= 6
    },
    confirmBtnText() {
      return this.currentStep === 1 ? 'Continuar' : '¡QUIERO MI COTIZACIÓN! '
    },

    currentField() {
      const map = {
        2: 'wantBuyTime',
        3: 'downPaymentAmount',
        4: 'monthlyIncome',
        5: 'credit',
        6: 'consider',
      }
      return map[this.currentStep]
    },

    currentValue() {
      return this.formValue[this.currentField]
    },

    currentOptions() {
      const map = {
        wantBuyTime: this.timeOptions,
        downPaymentAmount: this.downPaymentOptions,
        monthlyIncome: this.monthlyIncomeOptions,
        credit: this.creditOptions,
        consider: this.considerOptions,
      }
      return map[this.currentField] || []
    },
  },

  beforeMount() {
    this.restoreFormData()
    this.restoreStepFromLocal()
  },
  beforeDestroy() {
    this.cleanup && this.cleanup()
  },

  methods: {
    show() {
      this.visible = true
      this.$nextTick(this.initPosition)
    },
    hide() {
      this.visible = false
      this.cleanup && this.cleanup()
      this.cleanup = null
    },
    initPosition() {
      const reference = this.$refs.reference
      const floating = this.$refs.floating
      if (!reference || !floating) return

      this.cleanup = autoUpdate(reference, floating, () => {
        computePosition(reference, floating, {
          placement: 'top',
          middleware: [
            offset(10),
            flip(),
            shift({padding: 8})
          ]
        }).then(({x, y}) => {
          Object.assign(floating.style, {
            left: `${x}px`,
            top: `${y}px`
          })
        })
      })
    },
    isFormDataValid() {
      for (const stepCfg of this.STEP_CONFIG) {
        if (stepCfg.step === this.totalStep) continue  // 最后一步不校验
        // 必填字段必须存在
        for (const field of stepCfg.fields) {
          if (!(field in this.formValue)) {
            return false
          }
        }

        // step1 特殊处理 carBrand/carModel
        if (stepCfg.step === 1 && this.formValue.carBrand) {
          const brand = this.brandOptions.find(b => b.value === this.formValue.carBrand)
          if (!brand) return false
          if (this.formValue.carModel && !brand.children.some(m => m.value === this.formValue.carModel)) {
            return false
          }
        }

        // 校验值合法性（非空字段）
        if (stepCfg.optionsKey) {
          const options = this[stepCfg.optionsKey]
          const value = this.formValue[stepCfg.fields[0]]
          if (value && !options.some(opt => opt.value === value)) {
            return false
          }
        }

        // 自定义 validate
        if (stepCfg.validate) {
          const hasAllFields = stepCfg.fields.every(
              f => this.formValue[f] !== null && this.formValue[f] !== ''
          )

          if (hasAllFields && !stepCfg.validate(this.formValue, this)) {
            return false
          }
        }
      }

      return true
    },


    restoreFormData() {
      try {
        const data = JSON.parse(localStorage.getItem('formData'))
        if (data) {
          this.formValue = {...data}
        }
      } catch (e) {
      }

      if (this.formValue.carBrand) {
        const brand = this.brandOptions.find(b => b.value === this.formValue.carBrand)
        this.modelOptions = brand ? brand.children : []
      }
    },
    restoreStepFromLocal() {
      // 先检查数据是否完整合法
      if (!this.isFormDataValid()) {
        this.resetForm()
        return
      }

      // 找第一个未完成的 step
      for (let i = 0; i < this.STEP_CONFIG.length; i++) {
        const {step, isComplete} = this.STEP_CONFIG[i]
        if (step === this.totalStep) {
          this.currentStep = step
          return
        }

        if (!isComplete(this.formValue)) {
          this.currentStep = step
          return
        }
      }

      // 全部完成
      this.currentStep = this.totalStep
    },
    validate() {
      this.hide()
      if (this.currentStep === 1) {
        if (!this.formValue.carBrand) {
          this.showSnackbar('Seleccione la marca del auto que desea.')
          return
        } else if (!this.formValue.carModel) {
          this.showSnackbar('Seleccione el modelo del auto que desea.')
          return
        }
        this.$tracker.track(EVENTS.CLICK, {
          widgetId: "next-button",
          widgetLabel: 'Continuar',
        })
        this.nextStep()
        return
      } else if (this.currentStep === 7) {
        if (!this.formValue.phoneNo) {
          this.showSnackbar('Por favor, ingresa tu número celular.')
          return
        } else if (this.formValue.phoneNo.length < 10) {
          this.showSnackbar('El número de celular tiene 10 dígitos.')
          return
        }
        if (!this.formValue.userName) {
          this.showSnackbar('Por favor, ingresa tu nombre.')
          return
        } else if (this.formValue.userName) {
          if (!(/^[A-Za-z ]+$/.test(this.formValue.userName))) {
            this.showSnackbar('El nombre solo permite letras y espacios.')
            return
          } else if (this.formValue.userName.length < 2) {
            this.showSnackbar('El nombre debe tener al menos 2 caracteres.')
            return
          } else if (90 < this.formValue.userName.length) {
            this.showSnackbar('El nombre puede tener un máximo de 90 caracteres.')
            return
          }
        }
        if (this.formValue.email) {
          if (!(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(this.formValue.email))) {
            this.showSnackbar('El formato del correo electrónico es incorrecto.')
            return
          } else if (199 < this.formValue.email.length) {
            this.showSnackbar('El número de caracteres del correo electrónico excede el límite.')
            return
          }
        }
      }
      this.submit()
    },

    generateSign(formValue) {
      const timestamp = Date.now()

      // 1. 拿到 formValue 所有 key
      const keys = Object.keys(formValue)
          .filter(key => formValue[key] !== undefined && formValue[key] !== null) // 忽略 undefined / null
          .sort((a, b) => a.localeCompare(b)) // 按首字符排序

      // 2. 拼接值
      const concatenated = keys.map(key => {
        const val = formValue[key]
        return typeof val === 'string' ? val.trim() : val
      }).join('')

      // 3. 加上 timestamp
      const strToSign = concatenated + timestamp

      // 4. MD5
      const sign = CryptoJS.MD5(strToSign).toString()

      return {sign, timestamp}
    },
    submit() {
      if (this.currentStep === 7 && !this.isChecked) {
        this.show()
        return
      }
      this.loading = true
      const data = {...this.formValue, channelNo: 'NewCarLoan-V2-H5'}
      const {sign, timestamp} = this.generateSign(data)
      const url = `${process.env.VUE_APP_DOMAIN}/api/user/info/save/v2`
      axios.post(url, data, {
        headers: {
          'Content-Type': 'application/json',
          timestamp: timestamp,
          sign: sign,
        },
      })
          .then(res => {
            const data = res.data
            if (data.errorCode == 30002) {
              this.repeatDialogVisible = true
            } else if (data.errorCode == 0) {
              this.successDialogVisible = true
              this.$marketing.trackEvent("fcu2bz", {
                callbackParams: [
                  {key: 'biz_user_id', value: this.formValue.phoneNo}
                ]
              });
              if (!(this.formValue.downPaymentAmount === 'Prefiero un plan sin pago inicial' || (this.formValue.downPaymentAmount === 'Entre $1,000 y $30,000' && this.formValue.consider === 'No'))) {
                this.$marketing.trackEvent("8pnkpk", {
                  callbackParams: [
                    {key: 'biz_user_id', value: this.formValue.phoneNo}
                  ]
                });
              }
              this.$tracker.track(EVENTS.ERROR, {
                widgetId: 'submit-button',
                widgetLabel: '¡QUIERO MI COTIZACIÓN! '
              });
            } else {
              this.showSnackbar("La transmisión de datos ha fallado. Por favor, intente nuevamente.");
              this.$tracker.track(EVENTS.ERROR, {
                type: ERROR_TYPES.API_ERROR,
                message: data.message,
                errorCode: data.errorCode
              });
            }
          })
          .catch(() => {
            this.showSnackbar("La transmisión de datos ha fallado. Por favor, intente nuevamente.");
          })
          .finally(_ => {
            this.loading = false
            this.$tracker.manualFlush()
          })
    },

    handleBrandChange(value) {
      this.modelOptions =
          this.brandOptions.find(item => item.value === value)?.children || []
      this.formValue.carModel = null
      this.$tracker.track(EVENTS.CLICK, {
        widgetId: "carBrand-select",
        widgetLabel: value,
        eventValue: value,
      })

      this.saveLocal()
    },
    handleModelChange(value) {
      this.$tracker.track(EVENTS.CLICK, {
        widgetId: "carModel-select",
        widgetLabel: value,
        eventValue: value,
      })
      this.saveLocal()
    },
    handleRadioInput(option) {
      this.formValue[this.currentField] = option
      this.$tracker.track(EVENTS.CLICK, {
        widgetId: `${this.currentField}-radio`,
        widgetLabel: option,
        eventValue: option,
      })
      this.nextStep()
    },
    handleInput(e) {
      const input = e.target;
      // 直接获取原生值并过滤
      const val = input.value.replace(/[^\d]/g, '');

      // 同步更新数据
      this.formValue.phoneNo = val;

      // 关键：强制同步 DOM 元素的值，防止非法字符闪现
      // 这能解决手机端输入法产生的“退格”或“自动修正”问题
      if (input.value !== val) {
        input.value = val;
      }
    },
    handleBlur(e, type) {
      if(type === 'userName'){
        this.formValue[type] = this.formValue[type].trim()
      }
      const cur = this.formValue[type]
      if (cur && cur !== this.oldFormValue[type]) {
        this.$tracker.track(EVENTS.INPUT, {
          widgetId: `${type}-input`,
          eventValue: cur
        })
        this.oldFormValue[type] = cur
        this.saveLocal()
      }
    },
    handleBack() {
      if (this.currentStep !== 1) {
        this.$tracker.track(EVENTS.CLICK, {
          widgetId: `back-button`,
          eventValue: this.currentStep
        })
      }

      this.currentStep = Math.max(1, this.currentStep - 1)
      this.saveLocal()
    },
    nextStep() {
      if (this.currentStep < this.totalStep) {
        this.currentStep++
      }
      this.saveLocal()
    },

    saveLocal() {
      localStorage.setItem('formData', JSON.stringify(this.formValue))
      localStorage.setItem('currentStep', this.currentStep)
    },

    showSnackbar(text) {
      this.snackbar = false
      this.$nextTick(() => {
        this.snackbarText = text
        this.snackbar = true
      })

    },
    resetForm() {
      this.formValue = {
        carBrand: null,
        carModel: null,
        wantBuyTime: "",
        downPaymentAmount: "",
        monthlyIncome: "",
        credit: "",
        consider: "",
      }
      this.currentStep = 1
      localStorage.removeItem('formData')
      localStorage.removeItem('currentStep')
    },
    handleProtocolClick() {
      this.hide()
      this.isChecked = !this.isChecked
      this.$tracker.track(EVENTS.CLICK, {
        widgetId: "protocol-radio",
      })
    },
    handleSuccessConfirm() {
      this.resetForm()
      window.location.href = `${process.env.VUE_APP_DOMAIN}/calculator`;
    }
  },
}
</script>


<style scoped lang="less">
.forms__main {
  padding: 1rem;
}

.forms__backbtn {
  width: 1.75rem !important;
  height: 1.75rem !important;
  padding: 0 !important;
  min-width: unset !important;
  border-radius: 50%;

  &.v-btn.v-btn--has-bg {
    background-color: #E2E8F0 !important;

    .v-icon {
      color: #020617 !important;
    }
  }

  &.v-btn.v-btn--disabled.v-btn--has-bg {
    background-color: #E2E8F0 !important;

    .v-icon {
      color: #BDC7D6 !important;
    }
  }
}

.forms__title {
  color: #020617;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5rem;
  margin-top: 1rem;
}

.forms__title-tip {
  margin-top: 0.25rem;
  color: #0AAC5F;
  font-size: 0.6875rem;
  line-height: 1rem;
  display: flex;
  align-items: center;

  img {
    margin-right: 0.25rem;
  }
}

.forms__detail {
  margin: 1rem 0;


  .forms__detail-base {
    border: 1px solid #BDC7D6;
    background: #FFF;
    border-radius: 0.625rem;

    & > :not(:last-child) {
      border-bottom: 1px solid #BDC7D6;
    }

    .v-input {
      padding: 0 0.45rem;
      margin-top: 0;
      color: #020617;
      font-size: 0.9375rem;
      line-height: 1.25rem;


      :deep {


        .v-input__control {
          border-radius: 0.25rem;

          .v-input__slot {
            background: #ffffff !important;
            margin: 0.1rem 0;
            min-height: 2.5rem;
            padding: 0 0.3rem !important;


            &::before,
            &::after {
              display: none;
            }

            .v-label {
              color: #475569 !important;
              font-size: 0.9375rem;
              line-height: 1.25rem;

              &.v-label--active {
                line-height: 1rem;
              }
            }

            .v-text-field__slot {
              .v-text-field__suffix {
                margin-top: 0;
                color: #95A3B8;
                font-size: 0.75rem;
                line-height: 1rem;
              }
            }
          }

          .v-icon {
            color: #020617 !important
          }
        }
      }
    }
  }
}

.forms__main-submit {
  padding: 1rem 0;

  .v-btn {
    padding: 0.75rem 1rem !important;
    color: #FFF;
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5rem;
    height: unset !important;
    border-radius: 0.75rem;
  }
}

.forms__snackbar {
  :deep {
    .v-snack__wrapper {
      border-radius: 0.375rem;
      padding: 0.5rem 0.62rem;
      margin: 1rem;
      min-height: unset;
      min-width: unset;

      .v-snack__content {
        color: #FFF;
        font-size: 0.75rem;
        line-height: 1rem;
        padding: 0;
      }
    }
  }
}

.forms__protocol {
  position: relative;
  padding-top: 1rem;
  display: flex;

  .forms__protocol-img {
    cursor: pointer;
    margin-right: 0.25rem;
    align-self: flex-start;
  }

  .forms__protocol-text {
    color: #64748B;
    font-size: 0.6875rem;
    line-height: 1rem;
  }
}

.forms__protocol-tooltip {
  position: absolute;
  z-index: 9999;
  padding: 0.5rem 0.62rem;
  background: rgba(12, 21, 44, 0.8);
  color: #fff;
  border-radius: 0.375rem;
  white-space: nowrap;
  transform-origin: center;
  font-size: 0.75rem;
  line-height: 1rem;

  &::after {
    content: '';
    position: absolute;
    left: 1rem;
    bottom: -6px;
    transform: translateX(-50%);
    width: 0;
    height: 0;

    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(12, 21, 44, 0.80);
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(4px);
}

.phone-input-wrap {
  padding: 0 0.75rem;
}

.phone-input-main {
  position: relative;
  height: 3.375rem;
  margin: 0.125rem 0;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
  box-sizing: border-box;
}


/* 标签样式 - 模拟 Vuetify 浮动效果 */
.phone-input-label {
  position: absolute;
  left: 0;
  right: auto;
  top: 18px;
  color: rgba(0, 0, 0, 0.6);
  transition: .3s cubic-bezier(.25, .8, .5, 1);
  pointer-events: none;
  color: #475569;
  font-size: 0.9375rem;
  line-height: 1.25rem;
  height: 1.25rem;
  transform-origin: top left;
}

/* 激活状态下的标签 (缩小并上浮) */
.is-focused .phone-input-label,
.has-value .phone-input-label {
  transform: translateY(-6px) scale(0.75);
  color: #475569;
  line-height: 1rem;
}

/* 输入框原生样式重置 */
.phone-native-input {
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  padding-top: 1.25rem;
  caret-color: #000;
  /* 关键：开启抗锯齿渲染 */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color:#020617;
  font-family: Poppins;
  font-size: 15px;
  line-height: 20px;
  text-align: left;
  letter-spacing: normal;
}

/* 后缀样式 */
.phone-input-suffix {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

</style>
